import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { Shield, ShieldOff, Users, FileText, TrendingUp, BarChart3, Webhook, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Profile {
  id: string;
  email: string;
  created_at: string;
}

interface UserWithRole extends Profile {
  is_admin: boolean;
}

interface Stats {
  total_users: number;
  total_claims: number;
  pending_claims: number;
  approved_claims: number;
  denied_claims: number;
  in_review_claims: number;
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [webhookResult, setWebhookResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user?.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast.error("Access denied: Admin privileges required");
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
      fetchData();
    } catch (error: any) {
      console.error("Error checking admin status:", error);
      toast.error("Failed to verify admin access");
      navigate("/dashboard");
    }
  };

  const fetchData = async () => {
    try {
      const [profilesResult, rolesResult, claimsResult] = await Promise.all([
        supabase.from("profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id, role").eq("role", "admin"),
        supabase.from("claims").select("status")
      ]);

      if (profilesResult.error) throw profilesResult.error;
      if (rolesResult.error) throw rolesResult.error;
      if (claimsResult.error) throw claimsResult.error;

      const adminIds = new Set(rolesResult.data.map(r => r.user_id));
      const usersWithRoles = profilesResult.data.map(profile => ({
        ...profile,
        is_admin: adminIds.has(profile.id)
      }));

      setUsers(usersWithRoles);

      // Calculate stats
      const claims = claimsResult.data;
      setStats({
        total_users: profilesResult.data.length,
        total_claims: claims.length,
        pending_claims: claims.filter(c => c.status === "pending").length,
        approved_claims: claims.filter(c => c.status === "approved").length,
        denied_claims: claims.filter(c => c.status === "denied").length,
        in_review_claims: claims.filter(c => c.status === "in_review").length,
      });
    } catch (error: any) {
      toast.error("Failed to load data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminRole = async (userId: string, currentlyAdmin: boolean) => {
    try {
      if (userId === user?.id) {
        toast.error("You cannot modify your own admin status");
        return;
      }

      if (currentlyAdmin) {
        // Remove admin role
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", userId)
          .eq("role", "admin");

        if (error) throw error;
        toast.success("Admin privileges revoked");
      } else {
        // Add admin role
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: userId, role: "admin" });

        if (error) throw error;
        toast.success("Admin privileges granted");
      }

      fetchData();
    } catch (error: any) {
      toast.error("Failed to update user role");
      console.error("Error updating role:", error);
    }
  };

  const testWebhook = async () => {
    setWebhookTesting(true);
    setWebhookResult(null);
    
    try {
      const testPayload = {
        event: "property_submission",
        data: {
          id: 0,
          name: "Webhook Test Property",
          address: "123 Test Street",
          city: "Test City",
          state: "TX",
          submitted_at: new Date().toISOString(),
          status: "test"
        },
        timestamp: new Date().toISOString()
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wp-webhook`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(testPayload),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setWebhookResult({
          success: true,
          message: `Webhook is working! ${result.notifications_created || 0} notification(s) created.`
        });
        toast.success("WordPress webhook test successful!");
      } else {
        setWebhookResult({
          success: false,
          message: result.error || "Webhook returned an error response"
        });
        toast.error("Webhook test failed");
      }
    } catch (error: any) {
      console.error("Webhook test error:", error);
      setWebhookResult({
        success: false,
        message: error.message || "Failed to connect to webhook endpoint"
      });
      toast.error("Failed to test webhook");
    } finally {
      setWebhookTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Admin Panel</h1>
              <p className="text-muted-foreground">
                Manage users and view system statistics
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={testWebhook} 
                variant="outline"
                disabled={webhookTesting}
              >
                {webhookTesting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Webhook className="mr-2 h-4 w-4" />
                )}
                Test WP Webhook
              </Button>
              <Button onClick={() => navigate("/analytics")} variant="outline">
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </Button>
            </div>
          </div>
        </div>

        {/* Webhook Test Result */}
        {webhookResult && (
          <Alert 
            variant={webhookResult.success ? "default" : "destructive"} 
            className="mb-6"
          >
            {webhookResult.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <AlertTitle>
              {webhookResult.success ? "Webhook Test Passed" : "Webhook Test Failed"}
            </AlertTitle>
            <AlertDescription>{webhookResult.message}</AlertDescription>
          </Alert>
        )}

        {/* Statistics Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Registered accounts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Claims</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_claims || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                All submitted claims
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(stats?.pending_claims || 0) + (stats?.in_review_claims || 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Requires attention
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.total_claims && stats.total_claims > 0
                  ? Math.round((stats.approved_claims / stats.total_claims) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats?.approved_claims || 0} approved
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Claims Status Breakdown */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Claims by Status</CardTitle>
            <CardDescription>Overview of claim statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold">{stats?.pending_claims || 0}</p>
                </div>
                <Badge variant="outline">Pending</Badge>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">In Review</p>
                  <p className="text-2xl font-bold">{stats?.in_review_claims || 0}</p>
                </div>
                <Badge variant="secondary">In Review</Badge>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold">{stats?.approved_claims || 0}</p>
                </div>
                <Badge variant="default">Approved</Badge>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Denied</p>
                  <p className="text-2xl font-bold">{stats?.denied_claims || 0}</p>
                </div>
                <Badge variant="destructive">Denied</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Management */}
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              Manage user permissions and roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell className="font-medium">
                        {profile.email}
                      </TableCell>
                      <TableCell>
                        {profile.is_admin ? (
                          <Badge variant="default" className="gap-1">
                            <Shield className="h-3 w-3" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant="outline">User</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(profile.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        {profile.id !== user?.id && (
                          <Button
                            variant={profile.is_admin ? "destructive" : "default"}
                            size="sm"
                            onClick={() => toggleAdminRole(profile.id, profile.is_admin)}
                          >
                            {profile.is_admin ? (
                              <>
                                <ShieldOff className="h-4 w-4 mr-2" />
                                Revoke Admin
                              </>
                            ) : (
                              <>
                                <Shield className="h-4 w-4 mr-2" />
                                Make Admin
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AdminPanel;
